import { ConnectionState, Room } from "livekit-client";
import { useEffect, useRef } from "react";

import {
  nextAwayReason,
  type ProctoringAwayReason,
} from "@/src/helpers/proctoring-away-reason";
import {
  ParticipantTabChangedWebhookPayload,
  sendParticipantTabChangedWebhook,
} from "@/src/helpers/proctoring-webhook";

interface UseParticipantTabSwitchProctoringOptions {
  room: Room;
  roomName: string;
  roomType: string | null;
  meetId?: number;
  participantName?: string;
  isAdmin: boolean;
  enabled?: boolean;
}

const proctoringRoomTypes = ["voiceAssistant", "voiceAssistant2", "textAssistant"] as const;

type RoomTypeWithProctoring = (typeof proctoringRoomTypes)[number];

// Проктеринг включаем только для комнат с ассистентом
function isProctoringRoomType(roomType: string | null): roomType is RoomTypeWithProctoring {
  return Boolean(roomType && proctoringRoomTypes.includes(roomType as RoomTypeWithProctoring));
}

function parseParticipantMetadata(metadata?: string): unknown {
  if (!metadata) {
    return undefined;
  }

  try {
    return JSON.parse(metadata);
  } catch {
    return metadata;
  }
}

/**
 * Проверяет, является ли участник конференции кандидатом для проктеринга.
 *
 * Только кандидаты проверяются на проктеринг.
 */
function isCandidateParticipant(room: Room, isAdmin: boolean) {
  if (isAdmin) {
    return false;
  }

  const participant = room.localParticipant;
  const attributes = participant.attributes ?? {};
  const isAgent = (participant as { isAgent?: boolean }).isAgent === true;

  // Админы, кураторы и служебные участники не считаются кандидатами
  if (attributes.role === 'company_participant' || attributes.isAdmin === "true" || attributes.isCurator === "true") {
    return false;
  }

  if (
    isAgent ||
    participant.permissions?.agent ||
    participant.permissions?.recorder ||
    participant.permissions?.hidden
  ) {
    return false;
  }

  return true;
}

/**
 * Время в мс, в течение которого игнорируется потеря фокуса окна.
 *
 * Нужно, чтобы не ловить системные диалоги браузера (Save As, Print и т.д.)
 * и краткие переключения фокуса, не являющиеся уходом кандидата.
 */
const WINDOW_BLUR_GRACE_PERIOD_MS = 1_000;

/**
 * Хук, следящий за сменой вкладки участника в конференции.
 *
 * В случае обнаружения смены вкладки, отправляет событие в HURMA API,
 * чтобы оповестить об этом при формировании отчета по интервью.
 *
 * К payload вебхука отправляет ещё время отсутствия и причину: страница скрылась
 * или окно потеряло фокус, хотя вкладка могла остаться на экране.
 *
 * Отслеживает два сценария:
 * - Смена вкладки браузера / сворачивание окна (через `visibilitychange`)
 * - Переключение на другое приложение при видимой вкладке (через `window blur/focus`)
 */
export const useParticipantTabSwitchProctoring = ({
  room,
  roomName,
  roomType,
  meetId,
  participantName,
  isAdmin,
  enabled = true,
}: UseParticipantTabSwitchProctoringOptions) => {
  const hasReportedCurrentHiddenStateRef = useRef(false);
  const leftTabAtTimestampRef = useRef<number | null>(null);
  const awayReasonRef = useRef<ProctoringAwayReason | null>(null);

  useEffect(() => {
    if (!enabled || !isProctoringRoomType(roomType)) {
      return;
    }

    let isDisposed = false;
    let blurTimerId: ReturnType<typeof setTimeout> | null = null;

    const isConnectedCandidate = () =>
      room.state === ConnectionState.Connected && isCandidateParticipant(room, isAdmin);

    /**
     * Фиксирует начало ухода или повышает его причину.
     *
     * Если уход уже идёт, новый интервал не открываем, но потерю фокуса
     * можно сменить на скрытую страницу, когда человек ушёл на другую вкладку.
     */
    const markAsLeft = (reason: ProctoringAwayReason) => {
      awayReasonRef.current = nextAwayReason(awayReasonRef.current, reason);

      if (hasReportedCurrentHiddenStateRef.current) {
        return;
      }

      hasReportedCurrentHiddenStateRef.current = true;
      leftTabAtTimestampRef.current = Date.now();
    };

    const sendReturnWebhook = async () => {
      if (!hasReportedCurrentHiddenStateRef.current) {
        return;
      }

      const leftTabAtTimestamp = leftTabAtTimestampRef.current;

      if (!leftTabAtTimestamp) {
        return;
      }

      const roomSid = await room.getSid().catch(() => undefined);

      // Если компонент размонтировался во время получения sid, событие уже не актуально
      if (isDisposed) {
        return;
      }

      const returnedAtTimestamp = Date.now();
      const reason = awayReasonRef.current;

      if (!reason) {
        hasReportedCurrentHiddenStateRef.current = false;
        leftTabAtTimestampRef.current = null;
        awayReasonRef.current = null;

        return;
      }

      const payload: ParticipantTabChangedWebhookPayload = {
        event: "participant_tab_changed",
        room: {
          name: room.name || roomName,
          sid: roomSid,
          type: roomType,
          meet_id: meetId,
        },
        participant: {
          identity: room.localParticipant.identity,
          name: room.localParticipant.name || participantName,
          metadata: parseParticipantMetadata(room.localParticipant.metadata),
          attributes: room.localParticipant.attributes,
        },
        proctoring: {
          leftTabAtTimestamp,
          returnedAtTimestamp,
          wasAwayFromTabFor: returnedAtTimestamp - leftTabAtTimestamp,
          reason,
        },
      };

      // Сбрасываем состояние до await, чтобы повторный вызов не отправил дубликат
      hasReportedCurrentHiddenStateRef.current = false;
      leftTabAtTimestampRef.current = null;
      awayReasonRef.current = null;

      await sendParticipantTabChangedWebhook(payload);
    };

    const handleVisibilityChange = async () => {
      if (!isConnectedCandidate()) {
        return;
      }

      if (document.visibilityState === "hidden") {
        // Если был запущен таймер blur — отменяем, visibilitychange приоритетнее
        if (blurTimerId !== null) {
          clearTimeout(blurTimerId);
          blurTimerId = null;
        }

        markAsLeft("visibility_hidden");

        return;
      }

      if (document.visibilityState === "visible") {
        await sendReturnWebhook();
      }
    };

    // Срабатывает когда окно браузера теряет фокус, но вкладка остаётся видимой —
    // т.е. кандидат переключился на другое приложение.
    // Используем grace period, чтобы не ловить системные диалоги.
    const handleWindowBlur = () => {
      // Если вкладка уже скрыта — её уход уже зафиксирован через visibilitychange
      if (document.visibilityState !== "visible") {
        return;
      }

      if (!isConnectedCandidate() || hasReportedCurrentHiddenStateRef.current) {
        return;
      }

      blurTimerId = setTimeout(() => {
        blurTimerId = null;

        if (document.visibilityState === "visible") {
          markAsLeft("window_blur");
        }
      }, WINDOW_BLUR_GRACE_PERIOD_MS);
    };

    const handleWindowFocus = async () => {
      // Blur не успел сработать (был короче grace period) — игнорируем
      if (blurTimerId !== null) {
        clearTimeout(blurTimerId);
        blurTimerId = null;

        return;
      }

      // Если вкладка видима — visibilitychange не сработает, отправляем сами
      if (document.visibilityState === "visible") {
        await sendReturnWebhook();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleWindowBlur);
    window.addEventListener("focus", handleWindowFocus);

    return () => {
      isDisposed = true;

      if (blurTimerId !== null) {
        clearTimeout(blurTimerId);
      }

      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleWindowBlur);
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [enabled, isAdmin, meetId, participantName, room, roomName, roomType]);
};
